import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

declare global {
  interface Window {
    require?: {
      config?: (config: { paths: { [key: string]: string } }) => void;
      (dependencies: string[], callback: () => void): void;
    };
    monaco?: typeof import('monaco-editor');
  }
}

@Component({
  selector: 'app-monaco-editor',
  templateUrl: './monaco-editor.component.html',
  styleUrls: ['./monaco-editor.component.scss'],
  standalone: false,
})
export class MonacoEditorComponent implements AfterViewInit, OnChanges, OnDestroy {
  private static loaderPromise: Promise<typeof import('monaco-editor')>;

  @ViewChild('editorContainer', { static: true })
  editorContainer: ElementRef<HTMLDivElement>;

  @Input() value = '';
  @Input() language = 'html';
  @Input() height = '550px';
  @Input() options: import('monaco-editor').editor.IStandaloneEditorConstructionOptions = {};

  @Output() valueChange = new EventEmitter<string>();

  editor: import('monaco-editor').editor.IStandaloneCodeEditor;
  containerStyle: { [key: string]: string } = {};

  private resizeObserver?: ResizeObserver;
  private suppressChange = false;

  constructor(private readonly ngZone: NgZone) {}

  ngAfterViewInit(): void {
    this.containerStyle = { height: this.height };
    this.initializeEditor();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['height']) {
      this.containerStyle = { height: this.height };
      this.editor?.layout();
    }

    if (!this.editor) {
      return;
    }

    if (changes['language']) {
      const model = this.editor.getModel();
      if (model) {
        window.monaco?.editor.setModelLanguage(model, this.language || 'html');
      }
    }

    if (changes['options'] && !changes['options'].firstChange) {
      this.editor.updateOptions(this.options ?? {});
    }

    if (changes['value'] && changes['value'].currentValue !== this.editor.getValue()) {
      this.suppressChange = true;
      this.editor.setValue(changes['value'].currentValue ?? '');
      this.suppressChange = false;
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.editor?.dispose();
  }

  private async initializeEditor(): Promise<void> {
    const monaco = await MonacoEditorComponent.loadMonaco();
    const editorOptions: import('monaco-editor').editor.IStandaloneEditorConstructionOptions = {
      value: this.value ?? '',
      language: this.language || 'html',
      automaticLayout: false,
      minimap: { enabled: false },
      wordWrap: 'on',
      scrollBeyondLastLine: false,
      formatOnPaste: true,
      formatOnType: true,
      tabSize: 2,
      insertSpaces: true,
      ...this.options,
    };

    this.ngZone.runOutsideAngular(() => {
      this.editor = monaco.editor.create(this.editorContainer.nativeElement, editorOptions);

      this.editor.onDidChangeModelContent(() => {
        if (this.suppressChange) {
          return;
        }

        const nextValue = this.editor.getValue();
        this.ngZone.run(() => this.valueChange.emit(nextValue));
      });

      this.resizeObserver = new ResizeObserver(() => this.editor.layout());
      this.resizeObserver.observe(this.editorContainer.nativeElement);
      this.editor.layout();
    });
  }

  private static loadMonaco(): Promise<typeof import('monaco-editor')> {
    if (window.monaco) {
      return Promise.resolve(window.monaco);
    }

    if (!MonacoEditorComponent.loaderPromise) {
      MonacoEditorComponent.loaderPromise = new Promise((resolve, reject) => {
        window.MonacoEnvironment = { globalAPI: true };

        const onLoad = () => {
          if (!window.require?.config) {
            reject(new Error('Monaco AMD loader is not available.'));
            return;
          }

          window.require.config({ paths: { vs: 'assets/monaco-editor/vs' } });
          window.require(['vs/editor/editor.main'], () => {
            if (window.monaco) {
              resolve(window.monaco);
              return;
            }

            reject(new Error('Monaco failed to initialize.'));
          });
        };

        if (window.require?.config) {
          onLoad();
          return;
        }

        const existingLoader = document.querySelector<HTMLScriptElement>('script[data-monaco-loader="true"]');
        if (existingLoader) {
          existingLoader.addEventListener('load', onLoad, { once: true });
          existingLoader.addEventListener('error', () => reject(new Error('Failed to load Monaco loader script.')), { once: true });
          return;
        }

        const loaderScript = document.createElement('script');
        loaderScript.type = 'text/javascript';
        loaderScript.src = 'assets/monaco-editor/vs/loader.js';
        loaderScript.dataset['monacoLoader'] = 'true';
        loaderScript.addEventListener('load', onLoad, { once: true });
        loaderScript.addEventListener('error', () => reject(new Error('Failed to load Monaco loader script.')), { once: true });
        document.body.appendChild(loaderScript);
      });
    }

    return MonacoEditorComponent.loaderPromise;
  }
}
